"use client";

import React, { useState, useMemo, useEffect, useRef, useCallback } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { allUniversitiesFromPrograms as allUniversities } from "../calculator/adapter";
import { PROGRAMMES_DATA as PROGRAMS } from "../calculator/data";
import type { JupasProgram, CategoryLimits, MERResult, DSEResults, Grade } from "../types";
import { getUniversityKey } from "../common";
import { ScoreDistributionBar } from "./ScoreDistributionBar";
import { filterSubjectsByCategory, getSubjectCategory } from "@/lib/utils/subjectCategories";
import { appGetItem, appSetItem } from "@/lib/auth-storage";
import {
  getGradeValues,
  formatScore,
  filterForbiddenSubjects,
  check_dse_results,
  calcBestScore,
  calculateDetailedScore,
  calcSubjectMaxScore,
  calcHighestAttainableScore,
  type ScoreTriple,
  type SubjectBreakdownItem
} from "../calculator/calculator";
import { CORE_SUBJECTS } from "../common";
import { ExternalLink } from 'lucide-react';
import type { ProgrammeEntry } from "@/lib/core/types";
import { useFavourites } from "@/contexts/FavouritesContext";
import { useUserPreferences } from "@/contexts/UserPreferencesContext";
import { useLanguage } from "@/contexts/LanguageContext";
import { translate } from "@/lib/utils/translate";
import { Heart, ChevronUp, ChevronDown, ChevronsUpDown, LayoutGrid, Table2, Link as LinkIcon, Lock } from "lucide-react";
import { useSignInGate } from "@/hooks/useSignInGate";
import * as Switch from "@radix-ui/react-switch";
import Swal from "sweetalert2";
import { Tooltip, TooltipTrigger, TooltipContent } from "./ui/tooltip";
import { getUniversityFullName } from "@/lib/utils/universityNames";
import { getProgrammeName, getFacultyName, getLocalizedField } from "@/lib/utils/languageHelpers";
import { ProgrammeDetailsDialog } from "./ProgrammeDetailsDialog";
import { ScoreBadge } from "./ScoreBadge";
import { QuotaBadge } from "./QuotaBadge";
import { NewBadge } from "./NewBadge";
import { GerBadge, PerBadge } from "./GerPerBadges";
import { ResultsCardView } from "./ResultsCardView";
import { ResultsTableView } from "./ResultsTableView";
import { ALL_UNIVERSITIES } from "./UniversityFilterButtons";
import type { ScoreQuartileFilter, ScoreQuartileFilters } from "./ScoreQuartileFilterButtons";
import Image from "next/image";
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  flexRender,
  type ColumnDef,
  type SortingState,
  type ColumnFiltersState,
} from "@tanstack/react-table";

// Helper to remove JS prefix from Codes
const stripJSPrefix = (code: string | number): string => String(code).replace(/^JS/i, '');
// Rows (or cards) appended each time the infinite scroll reaches the bottom.
const LOAD_MORE_BATCH_SIZE = 5;
const ESTIMATED_CARD_HEIGHT = 280;
const ESTIMATED_TABLE_ROW_HEIGHT = 56;
const FALLBACK_RESULTS_PANE_HEIGHT = 555;

const TRACE_PROGRAMME_RESULTS = false;
const RESULTS_COUNT_PERSIST_DEBOUNCE_MS = 1200;
const resultsTraceNow = (): number => (typeof performance !== "undefined" ? performance.now() : Date.now());
const traceResults = (event: string, payload?: unknown) => {
  if (!TRACE_PROGRAMME_RESULTS || typeof window === "undefined") return;
  console.log(`[ResultsTrace] ${event}`, payload ?? "");
};
const warnResults = (event: string, payload?: unknown) => {
  if (!TRACE_PROGRAMME_RESULTS || typeof window === "undefined") return;
  console.warn(`[ResultsTrace] ${event}`, payload ?? "");
};

/*
 * Last completed scoring pass, kept at module scope so it outlives the component.
 *
 * `inputSignature` covers scores, selected electives and language, which is everything the pass
 * reads (`translate` is a pure function of language), so a matching signature means the pass would
 * produce exactly what is already here. Results are only ever read downstream — every sort copies
 * first — so handing the same array back is safe.
 */
let lastCalcSignature = "";
let lastCalcResults: ProgrammeResult[] = [];

// Pre-build O(1) lookup map for programmes (built once at module load)
const PROGRAMS_MAP = new Map<string, JupasProgram>();
for (const p of PROGRAMS) {
  PROGRAMS_MAP.set(p.jupas_id, p);
}

// Cache for calcHighestAttainableScore (programme-dependent only, not user-dependent)
const _highestAttainableScoreCache = new Map<string, number>();
function cachedCalcHighestAttainableScore(jupasProgram: JupasProgram | undefined, instKey: string): number {
  const key = jupasProgram?.jupas_id || '';
  if (_highestAttainableScoreCache.has(key)) return _highestAttainableScoreCache.get(key)!;
  const score = calcHighestAttainableScore(jupasProgram, instKey);
  _highestAttainableScoreCache.set(key, score);
  return score;
}

// Local interface (re-export from calculator for component use)
interface ResultsTableScoreTriple {
  current: string;
  min: string;
  max: string;
}

interface LocalScoreTriple {
  current: string;
  min: string;
  max: string;
}

type ScoreType = 'min' | 'current' | 'max';

interface ResultsTableProps {
  activeTab: "all" | "recommended" | "favourites" | "eligible" | "new";
  onTabChange: (tab: "all" | "recommended" | "favourites" | "eligible" | "new") => void;
  selectedElectives: string[];
  scores: Record<string, ScoreTriple>;
  globalFilter: string;
  setGlobalFilter: (value: string) => void;
  sorting: SortingState;
  setSorting: React.Dispatch<React.SetStateAction<SortingState>>;
  universityFilter: string;
  setUniversityFilter: (value: string) => void;
  scoreQuartileFilters: ScoreQuartileFilters;
  setScoreQuartileFilters: React.Dispatch<React.SetStateAction<ScoreQuartileFilters>>;
  onComputeProgress?: (processed: number, total: number, phase: string) => void;
}

export interface ProgrammeResult {
  id: string;
  university: string;
  prog_url: string;
  jupas_url: string;
  urls?: {
    programme?: string[];
    jupas?: string;
  };
  jupas_id: string;
  programmeName: string;
  new_programme?: boolean;
  minScore: number;
  currentScore: number;
  maxScore: number;
  highestAttainableScore: number;  // Maximum possible score for this programme (all 5**)
  score2025Lower: string;
  score2025Median: string;
  score2025Upper: string;
  scores: {
    "English Language": string;
    "Chinese Language": string;
    "Mathematics": string;
    "Citizenship and Social Development": string;
    elective1: string;
    elective2: string;
    elective3: string;
    elective4: string;
    elective5: string;
  };
  // Actual calculated scores for display
  calculatedScores: {
    "English Language": number;
    "Chinese Language": number;
    "Mathematics": number;
    "Citizenship and Social Development": string;
    bestElectives: Array<{
      subject: string;
      grade: string;
      score: number;
      baseScore?: number;
      weighting?: number;
    }>;
    allElectivesRanked: Array<{
      subject: string;
      grade: string;
      score: number;
      baseScore?: number;
      weighting?: number;
    }>;
    allSubjectsRanked?: Array<{
      subject: string;
      grade: string;
      score: number;
      baseScore?: number;
      weighting?: number;
    }>;
  };
  comments: string;
  isFavourite: boolean;
  isEligible: boolean;
  recommendationRate: number; // 0-1 score based on admission likelihood
  universityInfo: {
    name: string;
    jupasCode: string;
    programmeDetails: string;
    faculty: string;
    // websites: string[];
    period: string;
    interview: string;
  };
  admissionHistory: {
    year: string;
    LQ: string | number;
    MED: string | number;
    UQ: string | number;
    HA?: string | number;
  }[];
  requirements: {
    text: string;
    weightings: string;
  };
  // Store the original programme entry for context
  programmeEntry?: ProgrammeEntry;
  instKey?: string;
  first_year_intake?: string;
  // New fields from webapp2/PROGRAMS data
  programmeNote?: string;  // COMMENTS section (programme_notes)
  subjectMER?: string;          // SUBJECT (RAW) section (subject_mer)
  SpecificWeights?: Array<{        // FORMULA section (from specific_weights)
    type: string;
    subject?: string;
    weight?: number;
    groupName?: string;
    count?: number;
    mandatory?: boolean;          // If true, this subject must be included in calculation
    categoryLimits?: {            // Category restrictions for this slot
      max_category_b?: number;
      max_category_c?: number;
      max_combined_bc?: number;
      allowed_categories?: string[];
    };
  }>;
  subjectWeightings?: { [subject: string]: number };  // PolyU style subject weightings
  ger?: MERResult;  // General Entrance Requirements eligibility
  per?: MERResult;  // Programme Entrance Requirements eligibility
  forbiddenSubjectGroups?: string[][];  // Groups of subjects where only one can count
  programMER?: {  // Programme requirements payload for PER checking
    core_minima?: {
      "English Language"?: number | string;
      "Chinese Language"?: number | string;
      "Mathematics"?: number | string;
      "Citizenship and Social Development"?: number | string;
    };
    electives?: {
      count: number;
      min_level: number;
    };
    one_of?: {
      subjects: string[];
      min_level: number;
    };
  };
}
function parseInt1(str: string): string {
  return isNaN(Number(str)) ? '' : String(parseInt(str));
}
const gradeValues: Record<string, number> = {
  "5**": 7, "5*": 6, "5": 5, "4": 4, "3": 3, "2": 2, "1": 1, "U": 0
};

const hasEnteredScoresForType = (
  userScores: Record<string, ScoreTriple>,
  activeSubjects: string[],
  scoreType: Exclude<ScoreType, 'current'>,
): boolean => {
  return activeSubjects.some((subject) => {
    if (subject === 'Citizenship and Social Development') {
      return false;
    }

    const scoreTriple = userScores[subject];
    return Boolean(scoreTriple?.[scoreType]) && scoreTriple?.[scoreType] !== 'N/A';
  });
};

const mapBreakdownDetailsToRankedSubjects = (breakdownDetails: SubjectBreakdownItem[] | undefined) => {
  if (!breakdownDetails) {
    return undefined;
  }

  return breakdownDetails.map(item => ({
    subject: item.subject,
    grade: item.grade,
    score: item.score,
    baseScore: item.baseScore,
    weighting: item.weighting,
    mandatory: item.mandatory,
    slotType: item.slotType,
    category: item.category,
    counted: item.counted,
  }));
};

// Convert university data to display format
function convertUniversityDataToResults(
  userScores: Record<string, ScoreTriple>,
  selectedElectives: string[],
  language: 'en' | 'zh',
  translate: (key: any) => string,
  universities: Record<string, ProgrammeEntry[]> = allUniversities,
): ProgrammeResult[] {
  const results: ProgrammeResult[] = [];

  // Check if user has no electives selected - if so, set all recommendation scores to 0
  const hasNoElectives = selectedElectives.length === 0;

  // Category C languages that may have bonus scores
  const CATEGORY_C_LANGUAGES = ["French", "German", "Spanish", "Korean", "Japanese", "Urdu"];

  // NOTE: calcMaxProgScore function removed - using calcHighestAttainableScore from calculator.ts
  /* const calcMaxProgScore = (instKey: string, programme?: ProgrammeEntry): number => {
    let total = 0;
    const jupasProgram: any = PROGRAMS.find((p: any) => p.jupas_id === programme?.programmeDetails.id);
    const gradeValues = getGradeValues(jupasProgram?.score_system);

    // Check if user has selected any Category C languages
    const selectedCategoryCLanguages = selectedElectives.filter(subj => CATEGORY_C_LANGUAGES.includes(subj));

    // Check if new format (object with subject_rules)
    const isNewFormat = jupasProgram?.scoring_formula && 
                        typeof jupasProgram.scoring_formula === 'object' && 
                        !Array.isArray(jupasProgram.scoring_formula) && 
                        'subject_rules' in jupasProgram.scoring_formula;

    // Get subject weighting - use scoring_formula from jupasProgram first, then fall back to old data
    const getWeighting = (subjectName: string): number => {
      // First, try to get weight from scoring_formula (webapp2 data)
      if (jupasProgram?.scoring_formula && !isNewFormat) {
        const slot = (jupasProgram.scoring_formula as any[]).find((s: any) => s.subject === subjectName);
        if (slot && slot.weight) {
          return slot.weight;
        }
      }

      // Fall back to old specificSubjectWeighting if specific_weights not available
      if (!programme?.calculation?.specificSubjectWeighting) return 1.0;
      return programme.calculation.specificSubjectWeighting[subjectName] || 1.0;
    };

    // Get programme-specific grade_values
    let programmeGradeValues = jupasProgram?.grade_values;
    if (!programmeGradeValues && jupasProgram?.scoring_formula && typeof jupasProgram.scoring_formula === 'object' && !Array.isArray(jupasProgram.scoring_formula)) {
      programmeGradeValues = (jupasProgram.scoring_formula as any).grade_values;
    }
    const jupasId = programme?.programmeDetails.id.replace(/^JS/, '') || '';

    // Helper to get max score for a subject (using calcSubjectMaxScore)
    const getMaxSubjectScore = (subjectName: string, weight: number = 1.0): number => {
      return calcSubjectMaxScore(subjectName, instKey, jupasId, programmeGradeValues, weight);
    };

    // If scoring_formula exists, use it to determine the exact max score
    if (jupasProgram?.scoring_formula && !isNewFormat) {
      let usedCategoryCCount = 0;

      (jupasProgram.scoring_formula as any[]).forEach((slot: any) => {
        const weight = slot.weight || 1.0;

        if ((slot.type === "SPECIFIC" || slot.type === "CORE" || slot.type === "ELECTIVE_SPECIFIC") && slot.subject) {
          // Specific subject with its weight - use calcSubjectMaxScore
          const maxScore = getMaxSubjectScore(slot.subject, weight);
          total += maxScore;
        } else if (slot.type === "REMAINING" && slot.count) {
          // Remaining best N subjects - check for specific weights first
          const specificWeights = slot.weight_rules?.specific_weights || {};
          const programSubjectWeightings = (jupasProgram as any)?.subject_weightings || {};
          const defaultWeight = slot.weight_rules?.default_weight || weight || 1.0;
          const categoryLimits = slot.category_limits || slot.weight_rules?.category_limits;
          const maxCategoryC = categoryLimits?.max_category_c ?? 1;

          // For simplicity in max calculation, use the highest possible weight
          const slotWeightValues = Object.values(specificWeights).filter((w): w is number => typeof w === 'number');
          const programWeightValues = Object.values(programSubjectWeightings).filter((w): w is number => typeof w === 'number');
          const allWeights = [...slotWeightValues, ...programWeightValues];
          const maxWeight = Math.max(defaultWeight, ...allWeights);

          for (let i = 0; i < slot.count; i++) {
            // Check if we can use a Category C language for this slot
            if (usedCategoryCCount < maxCategoryC && usedCategoryCCount < selectedCategoryCLanguages.length) {
              const catCSubject = selectedCategoryCLanguages[usedCategoryCCount];
              const catCWeight = specificWeights[catCSubject] || programSubjectWeightings[catCSubject] || maxWeight;
              total += getMaxSubjectScore(catCSubject, catCWeight);
              usedCategoryCCount++;
            } else {
              // Use Category A max score (5**) with weight
              total += getMaxSubjectScore('English Language', maxWeight); // Use any Category A subject
            }
          }
        }
      });
      return total;
    }

    // Fall back to traditional calculation if no specific_weights
    // Core subjects with max grade and their weightings
    ["Chinese Language", "English Language", "Mathematics"].forEach(subject => {
      const weighting = getWeighting(subject);
      total += getMaxSubjectScore(subject, weighting);
    });

    // Best 2 electives with max grade and their weightings
    // Account for Category C languages if user has selected any
    const electiveSlots = 2;
    let usedCategoryCCount = 0;

    for (let i = 0; i < electiveSlots; i++) {
      if (usedCategoryCCount < selectedCategoryCLanguages.length && usedCategoryCCount < 1) {
        // Use Category C max score for first Category C language
        const catCSubject = selectedCategoryCLanguages[usedCategoryCCount];
        total += getMaxSubjectScore(catCSubject, 1.0);
        usedCategoryCCount++;
      } else {
        // Use Category A max score (5**)
        total += getMaxSubjectScore('English Language', 1.0); // Use any Category A subject
      }
    }

    return total;
  }; */

  // NOTE: calculateUserScore function removed - using calcUserScoreFromCalculator from calculator.ts
  /* const calculateUserScore = (instKey: string, programme?: ProgrammeEntry, scoreType: 'min' | 'current' | 'max' = 'max'): number => {
    const jupasProgram: any = PROGRAMS.find((p: any) => p.jupas_id === programme?.programmeDetails.id);
    const gradeValues = getGradeValues(jupasProgram?.score_system);

    // Check if new format (object with subject_rules)
    const isNewFormat = jupasProgram?.scoring_formula && 
                        typeof jupasProgram.scoring_formula === 'object' && 
                        !Array.isArray(jupasProgram.scoring_formula) && 
                        'subject_rules' in jupasProgram.scoring_formula;

    // Get subject weighting - use scoring_formula from jupasProgram first, then fall back to old data
    const getWeighting = (subjectName: string): number => {
      // First, try to get weight from scoring_formula (webapp2 data)
      if (jupasProgram?.scoring_formula && !isNewFormat) {
        const slot = (jupasProgram.scoring_formula as any[]).find((s: any) => s.subject === subjectName);
        if (slot && slot.weight) {
          return slot.weight;
        }
      }
      return 1;
    };

    // Check if this programme uses scoring_formula for calculation
    if (jupasProgram?.scoring_formula && !isNewFormat && (jupasProgram.scoring_formula as any[]).length > 0) {
      let total = 0;
      const usedSubjects = new Set<string>();

      // First, handle CORE and mandatory subjects
      const mandatorySlots = (jupasProgram.scoring_formula as any[]).filter((slot: any) =>
        slot.mandatory || slot.type === "CORE"
      );

      mandatorySlots.forEach((slot: any) => {
        if (slot.subject) {
          const score = userScores[slot.subject];
          if (score?.[scoreType]) {
            const baseScore = gradeValues[score[scoreType]] || 0;
            const weight = slot.weight || 1.0;
            total += baseScore * weight;
            usedSubjects.add(slot.subject);
          }
        }
      });

      // Then, handle REMAINING slots (Best N from remaining subjects)
      const remainingSlots = (jupasProgram.scoring_formula as any[]).filter((slot: any) =>
        slot.type === "REMAINING"
      );

      remainingSlots.forEach((slot: any) => {
        const bestNCount = slot.count || 0;
        const specificWeights = slot.weight_rules?.specific_weights || {};
        const defaultWeight = slot.weight_rules?.default_weight || 1.0;
        const categoryLimits: CategoryLimits | undefined = slot.category_limits || slot.weight_rules?.category_limits;
        // PolyU style subject_weightings at programme level
        const programSubjectWeightings = jupasProgram?.subject_weightings || {};

        // Helper to get weight for a subject (check slot-level, then programme-level, then default)
        const getSubjectWeight = (subjectName: string): number => {
          if (specificWeights[subjectName]) return specificWeights[subjectName];

          // Handle M1/M2 alias
          if ((subjectName === "Mathematics Extended Part Module 1" || subjectName === "Mathematics Extended Part Module 2") && specificWeights["M1/M2"]) {
            return specificWeights["M1/M2"];
          }

          if (programSubjectWeightings[subjectName]) return programSubjectWeightings[subjectName];

          // Handle M1/M2 alias for programSubjectWeightings (PolyU uses long names)
          if (subjectName === "Mathematics Extended Part Module 1" && programSubjectWeightings["Mathematics (Extended part - Calculus and Statistics)"]) {
            return programSubjectWeightings["Mathematics (Extended part - Calculus and Statistics)"];
          }
          if (subjectName === "Mathematics Extended Part Module 2" && programSubjectWeightings["Mathematics (Extended part - Algebra and Calculus)"]) {
            return programSubjectWeightings["Mathematics (Extended part - Algebra and Calculus)"];
          }

          return defaultWeight;
        };

        // Collect all available subjects (excluding already used ones)
        const allSubjectScores: Array<{ score: number; subject: string }> = [];

        // Add core subjects that haven't been used
        ["Chinese Language", "English Language", "Mathematics"].forEach(subject => {
          if (!usedSubjects.has(subject)) {
            const score = userScores[subject];
            if (score?.[scoreType]) {
              const baseScore = gradeValues[score[scoreType]] || 0;
              const weight = getSubjectWeight(subject);
              allSubjectScores.push({ score: baseScore * weight, subject });
            }
          }
        });

        // Add electives that haven't been used
        selectedElectives.forEach(subjectName => {
          if (!usedSubjects.has(subjectName)) {
            const score = userScores[subjectName];
            const grade = score?.[scoreType] || "";

            let baseScore = 0;
            if (["French", "German", "Spanish", "Korean", "Japanese", "Urdu"].includes(subjectName)) {
              baseScore = getCategoryCLanguageScore(grade, instKey, subjectName);
            } else {
              baseScore = grade ? gradeValues[grade] || 0 : 0;
            }

            const weight = getSubjectWeight(subjectName);
            allSubjectScores.push({ score: baseScore * weight, subject: subjectName });
          }
        });

        // Filter by category limits and take best N
        const bestN = filterSubjectsByCategory(allSubjectScores, categoryLimits, bestNCount);

        bestN.forEach(item => {
          total += item.score;
          usedSubjects.add(item.subject);
        });
      });

      return total;
    }

    // Check if this programme uses old "REMAINING" scoring (e.g., PolyU's "Any Best 5 Subjects")
    const scoringSlot = jupasProgram?.specific_weights?.[0];
    const isRemainingBestN = scoringSlot?.type === "REMAINING" && !jupasProgram?.specific_weights.some((s: any) => s.mandatory);
    const bestNCount = scoringSlot?.count || 5;

    if (isRemainingBestN && jupasProgram?.specific_weights.length === 1) {
      // For "Any Best N Subjects" - calculate all subjects and take the best N
      const allSubjectScores: Array<{ score: number; subject: string }> = [];

      // Add core subjects
      ["Chinese Language", "English Language", "Mathematics"].forEach(subject => {
        const score = userScores[subject];
        if (score?.[scoreType]) {
          const baseScore = gradeValues[score[scoreType]] || 0;
          const weighting = getWeighting(subject);
          allSubjectScores.push({
            score: baseScore * weighting,
            subject
          });
        }
      });

      // Add all electives
      selectedElectives.forEach(subjectName => {
        const score = userScores[subjectName];
        const grade = score?.[scoreType] || "";

        let baseScore = 0;
        if (["French", "German", "Spanish", "Korean", "Japanese", "Urdu"].includes(subjectName)) {
          baseScore = getCategoryCLanguageScore(grade, instKey, subjectName);
        } else {
          baseScore = grade ? gradeValues[grade] || 0 : 0;
        }

        const weighting = getWeighting(subjectName);
        allSubjectScores.push({
          score: baseScore * weighting,
          subject: subjectName
        });
      });

      // Sort by score descending and take best N
      const bestN = allSubjectScores
        .sort((a, b) => b.score - a.score)
        .slice(0, bestNCount);

      return bestN.reduce((sum, item) => sum + item.score, 0);
    }

    // Traditional calculation: 3 cores + best 2 electives
    let total = 0;

    // Core subjects - use specified scoreType, apply weighting
    ["Chinese Language", "English Language", "Mathematics"].forEach(subject => {
      const score = userScores[subject];
      if (score?.[scoreType]) {
        const baseScore = gradeValues[score[scoreType]] || 0;
        const weighting = getWeighting(subject);
        total += baseScore * weighting;
      }
    });

    // Electives (best 2) - use specified scoreType, apply weighting
    const electiveScores = selectedElectives
      .map(subjectName => {
        const score = userScores[subjectName];
        const grade = score?.[scoreType] || "";

        // Get base score
        let baseScore = 0;
        if (["French", "German", "Spanish", "Korean", "Japanese", "Urdu"].includes(subjectName)) {
          baseScore = getCategoryCLanguageScore(grade, instKey, subjectName);
        } else {
          baseScore = grade ? gradeValues[grade] || 0 : 0;
        }

        // Apply weighting for this specific elective
        const weighting = getWeighting(subjectName);
        return {
          score: baseScore * weighting,
          subject: subjectName
        };
      })
      .sort((a, b) => b.score - a.score)  // Sort descending by weighted score
      .slice(0, 2);  // Take best 2

    total += electiveScores.reduce((sum, item) => sum + item.score, 0);

    return total;
  }; */

  // Check if user meets basic eligibility (3322+2) using MAX expected grades only
  const checkEligibility_20260110 = (): boolean => {
    const gradeValues: Record<string, number> = {
      "5**": 7, "5*": 6, "5": 5, "4": 4, "3": 3, "2": 2, "1": 1, "U": 0
    };

    // Check 3322 requirement using MAX expected grades
    const chin = userScores["Chinese Language"]?.max;
    const eng = userScores["English Language"]?.max;
    const math = userScores["Mathematics"]?.max;
    const csd = userScores["Citizenship and Social Development"]?.max;

    if (!chin || !eng || !math || csd !== "Attained") return false;

    const chinGrade = gradeValues[chin] || 0;
    const engGrade = gradeValues[eng] || 0;
    const mathGrade = gradeValues[math] || 0;

    if (chinGrade < 3 || engGrade < 3 || mathGrade < 2) return false;

    // Check at least 2 electives with grade 2 or above using MAX expected
    const electiveGrades = selectedElectives
      .map(subjectName => {
        const score = userScores[subjectName];
        const grade = score?.max || "";

        // For Category C languages, use minimum threshold of 2 equivalent
        if (["French", "German", "Spanish", "Korean", "Japanese", "Urdu"].includes(subjectName)) {
          // Consider any recognized grade as meeting minimum
          return grade && grade !== "N/A" ? 2 : 0;
        }

        return grade ? gradeValues[grade] || 0 : 0;
      })
      .filter(grade => grade >= 2);

    return electiveGrades.length >= 2;
  };
  
  ///

  // Pre-build DSE results once outside the loop (only current grades needed for main computation)
  // Only include active subjects (core + selected electives) to avoid orphaned entries
  // from old preferences inflating scores.
  const activeSubjectNames = new Set([
    "Chinese Language", "English Language", "Mathematics", "Citizenship and Social Development",
    ...selectedElectives,
  ]);
  const dseResultsForCalc: Record<string, string> = {};
  Object.keys(userScores).forEach(subject => {
    if (!activeSubjectNames.has(subject)) return;
    const cur = userScores[subject]?.current;
    if (cur) dseResultsForCalc[subject] = cur;
  });

  Object.entries(universities).forEach(([uniKey, programmes]) => {

    programmes.forEach((prog: ProgrammeEntry) => {
      const programmeStartTime = resultsTraceNow();

      const jupas_id = String(prog.programmeDetails.id);
      const jupasProgram: any = PROGRAMS_MAP.get(jupas_id) || PROGRAMS_MAP.get(stripJSPrefix(jupas_id));
      traceResults("programme:start", {
        uniKey,
        jupasId: jupas_id,
        name: prog.programmeDetails.name_eng || prog.programmeDetails.name_tch || "",
      });

      // Extract minimum grade requirements from calculation conditions
      const getMinRequirement = (subjectCode: string): string => {
        // debugger;
        if (!prog.calculation?.conditions) return "-";

        const condition = prog.calculation.conditions.find(c => c.subject === subjectCode);
        if (condition) {
          if (condition.grade) return condition.grade;
          if (condition.minGrade) return condition.minGrade;
        }

        return "-";
      };

      // Extract elective requirements
      const getElectiveRequirements = (): string[] => {
        if (!prog.calculation?.conditions) return ["-", "-", "-", "-"];

        // Look for BEST4 or BEST5 conditions that specify elective requirements
        const best4Condition = prog.calculation.conditions.find(c => c.variable === "BEST4");
        const best5Condition = prog.calculation.conditions.find(c => c.variable === "BEST5");

        const electiveReq = best4Condition?.minGrade || best5Condition?.minGrade || "3";

        // Most CUHK programmes require 2-3 electives with min grade 3
        return [electiveReq, electiveReq, "-", "-"];
      };

      const electiveReqs = getElectiveRequirements();

      // NOTE: Eligibility now determined using calcResult.ger.eligible && calcResult.per.eligible (see below)

      // Get grade values based on programme's score system
      const programmeGradeValues = jupasProgram?.grade_values || jupasProgram?.scoring_formula?.grade_values;
      const gradeValues = getGradeValues(uniKey, jupas_id, programmeGradeValues);

      // Helper to normalize subject names
      const normalizeSubjectName = (name: string): string => {
        return name;
      };

      // Helper to get weighting for a subject - use specific_weights from jupasProgram first, then fall back to old data
      const getWeighting = (subjectName: string): number => {
        // First, try to get weight from specific_weights (webapp2 data)
        if (jupasProgram?.specific_weights) {
          const slot = jupasProgram.specific_weights.find((s: any) => s.subject === subjectName);
          if (slot && slot.weight) {
            return slot.weight;
          }
        }

        // Fall back to old specificSubjectWeighting if specific_weights not available
        if (!prog?.calculation?.specificSubjectWeighting) return 1.0;

        const normalizedName = normalizeSubjectName(subjectName);
        return prog.calculation.specificSubjectWeighting[normalizedName] || 1.0;
      };

      const engBaseScore = gradeValues[userScores["English Language"]?.current] || 0;
      const chinBaseScore = gradeValues[userScores["Chinese Language"]?.current] || 0;
      const mathBaseScore = gradeValues[userScores["Mathematics"]?.current] || 0;
      const csdGrade = userScores["Citizenship and Social Development"]?.current || "";

      // Apply weightings to core subjects
      const engScore = engBaseScore * getWeighting("English Language");
      const chinScore = chinBaseScore * getWeighting("Chinese Language");
      const mathScore = mathBaseScore * getWeighting("Mathematics");

      // Calculate all electives with their actual weighted scores, ranked
      const allElectivesRanked = selectedElectives
        .map(subjectName => {
          const score = userScores[subjectName];
          const grade = score?.current || "";

          // Get base score for this elective
          // let baseScore = 0;
          // if (["French", "German", "Spanish", "Korean", "Japanese", "Urdu"].includes(subjectName)) {
          //   baseScore = getCategoryCLanguageScore(grade, uniKey, subjectName);
          // } else {
          //   baseScore = grade ? gradeValues[grade] || 0 : 0;
          // }
          let baseScore = grade ? gradeValues[grade] || 0 : 0;

          // Apply weighting
          const weighting = getWeighting(subjectName);
          const weightedScore = baseScore * weighting;

          return {
            subject: subjectName,
            grade: grade,
            score: weightedScore,
            baseScore: baseScore,
            weighting: weighting
          };
        })
        .sort((a, b) => b.score - a.score);  // Sort by weighted score descending

      let programmeName = getProgrammeName(prog.programmeDetails, language);
      let faculty = getFacultyName(prog.programmeDetails, language);

      // Recommendation rate is now computed inside check_dse_results below — no need for separate meetsMinReqs/calcRecomRate here

      // Use shared calculation function - call once and reuse the result
      const calcStartTime = resultsTraceNow();
      const calcResult = check_dse_results({
        jupas_id: stripJSPrefix(jupas_id),  // Strip 'JS' prefix to match data.ts format
        dse_results: dseResultsForCalc as any,  // Cast to avoid type error - values are all valid Grade types
        programs: PROGRAMS as any[],
        program: jupasProgram,  // Pass pre-looked-up programme to avoid O(n) find
        fastMode: true,
      } as any);
      const calcDuration = resultsTraceNow() - calcStartTime;

      const highestScoreStartTime = resultsTraceNow();
      const highestAttainableScore = jupasProgram ? cachedCalcHighestAttainableScore(jupasProgram, getUniversityKey(jupas_id)) : 0;
      const highestScoreDuration = resultsTraceNow() - highestScoreStartTime;

      const totalProgrammeDuration = resultsTraceNow() - programmeStartTime;
      const programmeTracePayload = {
        uniKey,
        jupasId: jupas_id,
        name: programmeName,
        calcDurationMs: Number(calcDuration.toFixed(2)),
        highestAttainableScoreDurationMs: Number(highestScoreDuration.toFixed(2)),
        totalDurationMs: Number(totalProgrammeDuration.toFixed(2)),
        eligible: (calcResult?.ger?.eligible && calcResult?.per?.eligible) ?? false,
        currentScore: calcResult?.scores?.current ?? 0,
      };
      if (totalProgrammeDuration > 50 || calcDuration > 40) {
        warnResults("programme:slow", programmeTracePayload);
      } else {
        traceResults("programme:done", programmeTracePayload);
      }

      // debugger;

      // Programme is eligible only if it meets BOTH GER and PER
      const isProgrammeEligible = (calcResult?.ger?.eligible && calcResult?.per?.eligible) ?? false;

      // prog.programmeDetails.faculty || "Not specified"
      results.push({
        id: jupas_id,
        university: uniKey.toUpperCase(),
        jupas_id: jupas_id,
        programmeName: programmeName,
        new_programme: prog.programmeDetails.new_programme || false,
        prog_url: prog.programmeDetails.prog_url || "",
        jupas_url: prog.programmeDetails.jupas_url || "",
        urls: prog.programmeDetails.urls,
        first_year_intake: prog.programmeDetails.first_year_intake,

        currentScore: calcResult?.scores?.current ?? 0,
        minScore: calcResult?.scores?.current ?? 0,  // Use current as approximation (avoids 2 extra DFS per programme)
        maxScore: calcResult?.scores?.current ?? 0,  // Use current as approximation (avoids 2 extra DFS per programme)
        highestAttainableScore,

        score2025Lower: (prog.past_admission_scores?.[0] as any)?.LQ && (prog.past_admission_scores?.[0] as any)?.LQ !== 0 ? (prog.past_admission_scores?.[0] as any)?.LQ : "-",
        score2025Median: (prog.past_admission_scores?.[0] as any)?.MED && (prog.past_admission_scores?.[0] as any)?.MED !== 0 ? (prog.past_admission_scores?.[0] as any)?.MED : "-",
        score2025Upper: (prog.past_admission_scores?.[0] as any)?.UQ && (prog.past_admission_scores?.[0] as any)?.UQ !== 0 ? (prog.past_admission_scores?.[0] as any)?.UQ : "-",

        scores: {
          "English Language": getMinRequirement("English Language"),
          "Chinese Language": getMinRequirement("Chinese Language"),
          "Mathematics": getMinRequirement("Mathematics"),
          "Citizenship and Social Development": getMinRequirement("Citizenship and Social Development"),
          elective1: electiveReqs[0],
          elective2: electiveReqs[1],
          elective3: electiveReqs[2],
          elective4: electiveReqs[3],
          elective5: electiveReqs[4],
        },
        calculatedScores: {
          "English Language": engScore,
          "Chinese Language": chinScore,
          "Mathematics": mathScore,
          "Citizenship and Social Development": csdGrade,
          bestElectives: allElectivesRanked.slice(0, 2),  // Best 2 for score calculation
          allElectivesRanked: allElectivesRanked,  // All electives ranked for display
          // Use calcResult breakdown instead of recalculating
          allSubjectsRanked: (() => {
            if (!calcResult || !calcResult.scores.breakdownDetails) {
              // Fallback to traditional calculation if check_dse_results failed
              return [
                {
                  subject: "English Language",
                  grade: userScores["English Language"]?.current || "",
                  score: engScore,
                  baseScore: engBaseScore,
                  weighting: getWeighting("English Language"),
                  mandatory: true,
                  slotType: 'CORE',
                  category: 'A'
                },
                {
                  subject: "Chinese Language",
                  grade: userScores["Chinese Language"]?.current || "",
                  score: chinScore,
                  baseScore: chinBaseScore,
                  weighting: getWeighting("Chinese Language"),
                  mandatory: false,
                  slotType: 'CORE',
                  category: 'A'
                },
                {
                  subject: "Mathematics",
                  grade: userScores["Mathematics"]?.current || "",
                  score: mathScore,
                  baseScore: mathBaseScore,
                  weighting: getWeighting("Mathematics"),
                  mandatory: true,
                  slotType: 'CORE',
                  category: 'A'
                },
                ...allElectivesRanked.map(e => ({
                  ...e,
                  mandatory: false,
                  slotType: 'REMAINING',
                  category: 'A'
                }))
              ]
                .sort((a, b) => b.score - a.score)  // Sort all by weighted score
                .slice(0, 5);  // Take only best 5 subjects for display
            }

            // Convert breakdownDetails to our display format
            return (calcResult.scores.breakdownDetails || []).map((item: any) => ({
              subject: item.subject,
              grade: item.grade,
              score: item.weightedScore ?? item.score ?? 0,
              baseScore: item.rawScore ?? item.baseScore ?? 0,
              weighting: item.weight ?? item.weighting ?? 1,
              mandatory: item.mandatory,
              slotType: item.slotType,
              category: item.category
            }));
          })()
        },
        comments: getLocalizedField(prog, 'method', language) || '',
        isFavourite: false,
        isEligible: isProgrammeEligible,
        recommendationRate: calcResult?.recommendationRate || 0,
        universityInfo: {
          name: getUniversityFullName(uniKey, language),
          jupasCode: jupas_id,
          programmeDetails: programmeName,
          faculty: faculty || translate('notSpecified'),
          period: `${prog.programmeDetails.studyPeriod || 4} ${translate('years')}`,
          interview: getLocalizedField(prog.programmeDetails, 'interview', language)
            || (prog.programmeDetails.interviewRequired ? translate('required') : translate('notRequired')),
        },
        admissionHistory: (prog.past_admission_scores || []).map(score => {
          // console.log(`Programme ${jupas_id} mapping score:`, score);
          return {
            year: String(score.year),
            LQ: score.LQ && score.LQ !== 0 ? score.LQ : "N/A",
            MED: score.MED && score.MED !== 0 ? score.MED : "N/A",
            UQ: score.UQ && score.UQ !== 0 ? score.UQ : "N/A",
            HA: score.HA && score.HA !== 0 ? score.HA : "N/A",
          };
        }),
        requirements: {
          text: prog.otherRequirements?.join("; ") || translate('standardDseRequirements'),
          weightings: prog.method || translate('best5Subjects'),
        },
        // Store the original programme entry and university key
        programmeEntry: prog,
        instKey: uniKey,
        // Add webapp2 data fields
        programmeNote: jupasProgram?.programme_notes,
        subjectMER: jupasProgram?.subject_mer,
        subjectWeightings: jupasProgram?.subject_weightings,
        forbiddenSubjectGroups: jupasProgram?.forbidden_subject_groups,
        SpecificWeights: jupasProgram?.specific_weights?.map((slot: any) => ({
          type: slot.type,
          subject: slot.subject,
          weight: slot.weight,
          groupName: slot.group_name,
          count: slot.count,
          mandatory: slot.mandatory,
          categoryLimits: slot.category_limits || slot.weight_rules?.category_limits,
          weightRules: slot.weight_rules,
        })),
        programMER: jupasProgram?.program_mer as any,
        // Add GER and PER from calculation result
        ger: calcResult?.ger,
        per: calcResult?.per,
      });
    });
  });

  return results;
}

// Helper function to check if a programme has valid admission scores (not all N/A)
function hasValidAdmissionScores(admissionHistory: { year: string; LQ: string | number; MED: string | number; UQ: string | number; HA?: string | number }[]): boolean {
  if (!admissionHistory || admissionHistory.length === 0) return false;

  // Check if at least one year has valid numeric scores (not "N/A" and not 0)
  return admissionHistory.some(score => {
    const hasLQ = score.LQ !== "N/A" && score.LQ !== 0;
    const hasMED = score.MED !== "N/A" && score.MED !== 0;
    const hasUQ = score.UQ !== "N/A" && score.UQ !== 0;
    const hasHA = score.HA !== undefined && score.HA !== "N/A" && score.HA !== 0; // HKUST uses HA instead of UQ
    return hasLQ || hasMED || hasUQ || hasHA;
  });
}

function translateGradeValue(value: string, language: 'en' | 'zh'): string {
  // if (language === 'zh') {
  // if (value === 'Attained') return '達標';
  // if (value === 'Unattained') return '未達標';
  // }
  if (value === 'Attained') return 'A';
  if (value === 'Unattained') return 'U';
  return value;
}

// Format quota text - truncate with ellipsis if it contains brackets
function formatQuota(quota: string | undefined): { display: string; full: string; hasTooltip: boolean } {
  if (!quota) return { display: 'N/A', full: 'N/A', hasTooltip: false };

  // Check if quota contains brackets
  if (quota.includes('(') || quota.includes('[')) {
    // Find the position of the opening bracket
    const bracketIndex = Math.min(
      quota.indexOf('(') !== -1 ? quota.indexOf('(') : Infinity,
      quota.indexOf('[') !== -1 ? quota.indexOf('[') : Infinity
    );

    // Truncate at bracket and add ellipsis for display
    const display = quota.substring(0, bracketIndex).trim() + '...';

    // Return the full quota string for tooltip
    return { display, full: quota, hasTooltip: true };
  }

  return { display: quota, full: quota, hasTooltip: false };
}

///

const ResultsTable = React.memo(function ResultsTable({ activeTab, onTabChange, selectedElectives, scores, globalFilter, setGlobalFilter, sorting, setSorting, universityFilter, setUniversityFilter, scoreQuartileFilters, setScoreQuartileFilters, onComputeProgress }: ResultsTableProps) {
  // Dialog state for programme details popup
  const [dialogProgramme, setDialogProgramme] = useState<ProgrammeResult | null>(null);
  const [selectedScoreType, setSelectedScoreType] = useState<Record<string, 'min' | 'current' | 'max'>>({});
  const { favourites, addFavourite, removeFavourite, isFavourite } = useFavourites();
  const { preferences, updatePreferences } = useUserPreferences();
  const { language, translate } = useLanguage();
  // Favourites and Recommended are signed-in-only features (requirement 3).
  const { isSignedIn, requireSignIn } = useSignInGate();

  // View toggle state (true = table view, false = card view)
  // Initialize from the in-memory app store or default to card view.
  const [isTableView, setIsTableView] = useState(() => {
    if (typeof window === 'undefined') return false;
    const saved = appGetItem('toggle-card-table');
    if (saved === 'table') return true;
    if (saved === 'card') return false;
    return false;
  });
  const [isDesktop, setIsDesktop] = useState(true);
  const [hasUserPreference, setHasUserPreference] = useState(() => {
    if (typeof window === 'undefined') return false;
    return appGetItem('toggle-card-table') !== null;
  });

  // Infinite scroll state
  const [displayCount, setDisplayCount] = useState(20);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [containerHeight, setContainerHeight] = useState(0);

  const handleToggleView = (newValue: boolean) => {
    setIsTableView(newValue);
    appSetItem('toggle-card-table', newValue ? 'table' : 'card');
    setHasUserPreference(true);
  };

  // Calculate container height based on window size - recalculates on every resize
  useEffect(() => {
    const calculateHeight = () => {
      // Get the scroll container's position from top
      const container = scrollContainerRef.current;
      let totalHeight = 0;
      if (container) {
        totalHeight = container.clientHeight;
        if (!totalHeight) {
          const rect = container.getBoundingClientRect();
          totalHeight = window.innerHeight - rect.top - 20;
        }
      } else {
        totalHeight = FALLBACK_RESULTS_PANE_HEIGHT;
      }
      setContainerHeight(totalHeight);
    };

    // Initial calculation
    calculateHeight();

    // Recalculate on window resize
    window.addEventListener('resize', calculateHeight);

    // Recalculate after DOM is ready and on orientation change
    const timeout = setTimeout(calculateHeight, 100);
    window.addEventListener('orientationchange', calculateHeight);

    return () => {
      window.removeEventListener('resize', calculateHeight);
      window.removeEventListener('orientationchange', calculateHeight);
      clearTimeout(timeout);
    };
  }, []);

  // Recalculate height when view mode changes (card <-> table)
  useEffect(() => {
    const recalcTimeout = setTimeout(() => {
      const container = scrollContainerRef.current;
      if (container) {
        const rect = container.getBoundingClientRect();
        const availableHeight = window.innerHeight - rect.top - 20;
        // setContainerHeight(`${availableHeight}px`);
      }
    }, 50);

    return () => clearTimeout(recalcTimeout);
  }, [isTableView]);

  // Handle responsive view switching
  useEffect(() => {
    let previousDesktop: boolean | null = null;
    let isInitialLoad = true;

    const handleResize = () => {
      // const desktop = window.innerWidth >= 1024;

      // // Skip initial handleResize if user has a saved preference
      // if (isInitialLoad && hasUserPreference) {
      //   setIsDesktop(desktop);
      //   previousDesktop = desktop;
      //   isInitialLoad = false;
      //   return;
      // }

      // // Only update view if desktop state actually changed
      // if (previousDesktop !== null && previousDesktop !== desktop) {
      //   setIsDesktop(desktop);
      //   // Only auto-switch if user hasn't set a preference
      //   if (!hasUserPreference) {
      //     if (!desktop) {
      //       setIsTableView(false);  // Switch to card view on mobile
      //     } else {
      //       setIsTableView(true);   // Switch to table view on desktop
      //     }
      //   }
      // } else if (previousDesktop === null) {
      //   // Initial setup
      //   setIsDesktop(desktop);
      //   // Don't override if user has a saved preference
      //   if (!hasUserPreference) {
      //     setIsTableView(desktop);
      //   }
      // }      
      // previousDesktop = desktop;
      // isInitialLoad = false;
    };

    // Initial check
    handleResize();

    // Add event listener
    window.addEventListener('resize', handleResize);

    return () => window.removeEventListener('resize', handleResize);
  }, [hasUserPreference]);

  // TanStack Table states
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 20,
  });

  // Card view pagination
  const [cardsToShow, setCardsToShow] = useState(10);
  const [allResults, setAllResults] = useState<ProgrammeResult[]>([]);
  const [isCalculatingResults, setIsCalculatingResults] = useState(true);
  const [calculationProgress, setCalculationProgress] = useState({ processed: 0, total: 0 });
  const [fullTabCounts, setFullTabCounts] = useState({
    all: 0,
    recommended: 0,
    favourites: 0,
    eligible: 0,
    new: 0,
    processed: 0,
    total: 0,
    complete: false,
  });
  const [displayTabCounts, setDisplayTabCounts] = useState({
    all: 0,
    recommended: 0,
    favourites: 0,
    eligible: 0,
    new: 0,
  });
  const pendingResultsRef = useRef<ProgrammeResult[]>([]);
  const flattenedProgrammesRef = useRef<Array<{ uniKey: string; programme: ProgrammeEntry }>>([]);
  const programmeIndexRef = useRef(0);
  const flushedCountRef = useRef(0);
  const calculationRunIdRef = useRef(0);
  const calculationSignatureRef = useRef("");
  const lastPersistedCountsRef = useRef<{ homeResultsCount: number; homeEligibleCount: number } | null>(null);
  const persistCountsTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const inputSignature = useMemo(
    () => JSON.stringify({ scores, selectedElectives, language }),
    [scores, selectedElectives, language]
  );

  const flattenedProgrammes = useMemo(() => {
    const allProgrammes: Array<{ uniKey: string; programme: ProgrammeEntry }> = [];
    for (const [uniKey, programmes] of Object.entries(allUniversities)) {
      for (const programme of programmes) {
        allProgrammes.push({ uniKey, programme });
      }
    }
    return allProgrammes;
  }, []);

  /*
   * There used to be a `rankedProgrammesForLoading` memo here: a full
   * check_dse_results() pass over every programme, sorted by likelihood, to
   * decide the order the calculation below would walk them in. It cost about a
   * second of blocked main thread on every mount and every grade change, and
   * none of it reached the screen — the calculation regroups by university
   * (dropping the ranking), and what the user sees is ordered later by
   * `sortedResults` from the sort controls. The calculation now walks
   * `flattenedProgrammes` directly, scoring each programme once.
   */

  const availableProgrammeIdSet = useMemo(
    () => new Set(flattenedProgrammes.map(({ programme }) => stripJSPrefix(String(programme.programmeDetails.id)))),
    [flattenedProgrammes]
  );

  const favouriteProgrammeIdSet = useMemo(
    () => new Set(favourites.map(f => stripJSPrefix(String(f.programme.programmeDetails.id)))),
    [favourites]
  );

  const aiRecommendedProgrammeIdSet = useMemo(
    () => new Set(
      (preferences.recommendedRecommendations || [])
        .map((rec: any) => rec.programme?.programmeDetails?.id || rec.jupasCode)
        .filter(Boolean)
        .map((id: string) => stripJSPrefix(String(id)))
    ),
    [preferences.recommendedRecommendations]
  );

  const staticTabCounts = useMemo(() => ({
    all: flattenedProgrammes.length,
    recommended: aiRecommendedProgrammeIdSet.size > 0
      ? Array.from(aiRecommendedProgrammeIdSet).filter(id => availableProgrammeIdSet.has(id)).length
      : 0,
    favourites: Array.from(favouriteProgrammeIdSet).filter(id => availableProgrammeIdSet.has(id)).length,
    new: flattenedProgrammes.filter(({ programme }) => programme.programmeDetails.new_programme === true).length,
  }), [flattenedProgrammes, aiRecommendedProgrammeIdSet, favouriteProgrammeIdSet, availableProgrammeIdSet]);

  const effectivePaneHeight = useMemo(
    () => Math.max(containerHeight || FALLBACK_RESULTS_PANE_HEIGHT, FALLBACK_RESULTS_PANE_HEIGHT),
    [containerHeight]
  );

  const visibleCardCount = useMemo(
    () => Math.max(2, Math.ceil(effectivePaneHeight / ESTIMATED_CARD_HEIGHT)),
    [effectivePaneHeight]
  );

  const visibleTableRowCount = useMemo(
    () => Math.max(10, Math.ceil(effectivePaneHeight / ESTIMATED_TABLE_ROW_HEIGHT)),
    [effectivePaneHeight]
  );

  const initialCardCount = useMemo(() => visibleCardCount * 3, [visibleCardCount]);
  const initialTableCount = useMemo(() => visibleTableRowCount * 3, [visibleTableRowCount]);

  const hasMoreToCompute = calculationProgress.processed < calculationProgress.total;

  useEffect(() => {
    onComputeProgress?.(calculationProgress.processed, calculationProgress.total, hasMoreToCompute ? 'calc' : 'done');
  }, [calculationProgress.processed, calculationProgress.total, onComputeProgress, hasMoreToCompute]);

  // Score every programme. This is synchronous and blocks the main thread, so
  // it must not be started in the same frame as a paint the user is waiting on.
  useEffect(() => {
    const isNewCalculation = calculationSignatureRef.current !== inputSignature;
    if (!isNewCalculation) return;

    calculationSignatureRef.current = inputSignature;

    const allProgrammes = flattenedProgrammes;
    const total = allProgrammes.length;

    if (total === 0) {
      setAllResults([]);
      setCalculationProgress({ processed: 0, total: 0 });
      setIsCalculatingResults(false);
      return;
    }

    // Nothing about the inputs changed since the last calculation, so reuse it rather than paying
    // the pass again. `calculationSignatureRef` cannot do this on its own: it is a ref, so it dies
    // with the component, and this component is remounted on every navigation back to Home and
    // again on every grade change (`key={resultsKey}`).
    if (lastCalcSignature === inputSignature) {
      setAllResults(lastCalcResults);
      setCardsToShow(10);
      setDisplayCount(10);
      setCalculationProgress({ processed: lastCalcResults.length, total: lastCalcResults.length });
      setIsCalculatingResults(false);
      return;
    }

    setCalculationProgress({ processed: 0, total });
    setIsCalculatingResults(true);

    // Group all programmes by university for batch processing
    const grouped: Record<string, any[]> = {};
    for (const item of allProgrammes) {
      if (!grouped[item.uniKey]) grouped[item.uniKey] = [];
      grouped[item.uniKey].push(item.programme);
    }

    console.time('[perf] calc-all');
    const results: ProgrammeResult[] = [];
    try {
      for (const [uniKey, programmes] of Object.entries(grouped)) {
        const batch = convertUniversityDataToResults(
          scores, selectedElectives, language, translate,
          { [uniKey]: programmes },
        );
        results.push(...batch);
      }
    } catch (err) {
      console.error('[calc] ERROR:', err);
    }
    console.timeEnd('[perf] calc-all');
    console.log(`[calc] Done: ${results.length} results from ${total} programmes`);

    lastCalcSignature = inputSignature;
    lastCalcResults = results;

    console.time('[perf] setAllResults');
    setAllResults(results);
    setCardsToShow(10);
    setDisplayCount(10);
    setCalculationProgress({ processed: total, total });
    setIsCalculatingResults(false);
    console.timeEnd('[perf] setAllResults');
  }, [inputSignature, flattenedProgrammes]);

  // Compute exact tab counts from already-loaded allResults (no recalculation needed).
  useEffect(() => {
    if (hasMoreToCompute) {
      setFullTabCounts(prev => ({
        ...prev,
        all: staticTabCounts.all,
        recommended: staticTabCounts.recommended,
        favourites: staticTabCounts.favourites,
        eligible: allResults.filter(result => result.isEligible).length,
        new: staticTabCounts.new,
        processed: calculationProgress.processed,
        total: flattenedProgrammes.length,
        complete: false,
      }));
      return;
    }

    const eligibleCount = allResults.filter(result => result.isEligible).length;
    // Only show recommendation count when user has actually requested AI recommendations
    const recommendedCount = staticTabCounts.recommended;

    setFullTabCounts({
      all: staticTabCounts.all,
      recommended: recommendedCount,
      favourites: staticTabCounts.favourites,
      eligible: eligibleCount,
      new: staticTabCounts.new,
      processed: flattenedProgrammes.length,
      total: flattenedProgrammes.length,
      complete: true,
    });
  }, [inputSignature, flattenedProgrammes, staticTabCounts, aiRecommendedProgrammeIdSet, hasMoreToCompute, allResults]);

  useEffect(() => {
    if (hasMoreToCompute || !fullTabCounts.complete) {
      return;
    }

    const nextCounts = {
      homeResultsCount: fullTabCounts.all,
      homeEligibleCount: fullTabCounts.eligible,
    };

    const previousCounts = lastPersistedCountsRef.current;
    if (
      previousCounts &&
      previousCounts.homeResultsCount === nextCounts.homeResultsCount &&
      previousCounts.homeEligibleCount === nextCounts.homeEligibleCount
    ) {
      return;
    }

    if (persistCountsTimeoutRef.current) {
      clearTimeout(persistCountsTimeoutRef.current);
    }

    persistCountsTimeoutRef.current = setTimeout(() => {
      lastPersistedCountsRef.current = nextCounts;
      updatePreferences(nextCounts);
      persistCountsTimeoutRef.current = null;
    }, RESULTS_COUNT_PERSIST_DEBOUNCE_MS);

    return () => {
      if (persistCountsTimeoutRef.current) {
        clearTimeout(persistCountsTimeoutRef.current);
        persistCountsTimeoutRef.current = null;
      }
    };
  }, [fullTabCounts.all, fullTabCounts.complete, fullTabCounts.eligible, hasMoreToCompute, updatePreferences]);

  useEffect(() => {
    const liveCounts = {
      all: fullTabCounts.all,
      recommended: fullTabCounts.recommended,
      favourites: fullTabCounts.favourites,
      eligible: fullTabCounts.eligible,
      new: fullTabCounts.new,
    };

    if (hasMoreToCompute) {
      setDisplayTabCounts(prev => ({
        all: staticTabCounts.all,
        recommended: activeTab === 'recommended' ? liveCounts.recommended : staticTabCounts.recommended,
        favourites: staticTabCounts.favourites,
        eligible: activeTab === 'eligible' ? liveCounts.eligible : prev.eligible,
        new: staticTabCounts.new,
      }));
      return;
    }

    setDisplayTabCounts(prev => ({
      all: fullTabCounts.all,
      recommended: activeTab === 'recommended' || fullTabCounts.complete ? fullTabCounts.recommended : prev.recommended,
      favourites: fullTabCounts.favourites,
      eligible: activeTab === 'eligible' || fullTabCounts.complete ? fullTabCounts.eligible : prev.eligible,
      new: fullTabCounts.new,
    }));
  }, [activeTab, fullTabCounts, hasMoreToCompute, staticTabCounts]);

  // Reset cards to show when tab or search filter changes
  useEffect(() => {
    setCardsToShow(initialCardCount);
    setDisplayCount(initialTableCount);
    scrollContainerRef.current?.scrollTo({ top: 0 });
  }, [activeTab, globalFilter, universityFilter, initialCardCount, initialTableCount]);

  const loadMoreResults = useCallback(() => {
    if (isTableView) {
      setDisplayCount(prev => prev + LOAD_MORE_BATCH_SIZE);
    } else {
      setCardsToShow(prev => prev + LOAD_MORE_BATCH_SIZE);
    }
  }, [isTableView]);

  // Open dialog for programme details
  const openProgrammeDialog = useCallback((result: ProgrammeResult) => {
    const rawJupasId = String(result.programmeEntry?.programmeDetails.id || result.jupas_id);
    const strippedJupasId = stripJSPrefix(rawJupasId);
    const jupasProgram = PROGRAMS_MAP.get(rawJupasId) || PROGRAMS_MAP.get(strippedJupasId);
    const activeSubjects = [...CORE_SUBJECTS.map(subject => subject.subject), ...selectedElectives];

    // Filter scores to ONLY active subjects to prevent orphaned entries (subjects
    // removed from electives but still lingering in scores from old preferences)
    // from inflating the calculated score.
    const activeSubjectSet = new Set(activeSubjects);
    const activeScores = Object.fromEntries(
      Object.entries(scores).filter(([subject]) => activeSubjectSet.has(subject))
    );

    if (!jupasProgram) {
      setDialogProgramme(result);
      return;
    }

    const currentCalc = check_dse_results({
      jupas_id: strippedJupasId,
      dse_results: Object.fromEntries(
        Object.entries(activeScores)
          .filter(([, scoreTriple]) => Boolean(scoreTriple?.current) && scoreTriple.current !== 'N/A')
          .map(([subject, scoreTriple]) => [subject, scoreTriple.current as Grade])
      ) as DSEResults,
      programs: PROGRAMS as JupasProgram[],
      program: jupasProgram,
      fastMode: false,
    });

    const hasMinScores = hasEnteredScoresForType(activeScores, activeSubjects, 'min');
    const minScore = hasMinScores
      ? calcBestScore(strippedJupasId, activeScores, PROGRAMS as JupasProgram[], 'min')
      : 0;

    const hasMaxScores = hasEnteredScoresForType(activeScores, activeSubjects, 'max');
    const maxScore = hasMaxScores
      ? calcBestScore(strippedJupasId, activeScores, PROGRAMS as JupasProgram[], 'max')
      : 0;

    const currentScore = calcBestScore(strippedJupasId, activeScores, PROGRAMS as JupasProgram[], 'current');
    const currentBreakdown = calculateDetailedScore(
      activeScores,
      selectedElectives,
      jupasProgram,
      getUniversityKey(result.jupas_id),
      'current'
    );

    setDialogProgramme({
      ...result,
      currentScore: currentScore || result.currentScore,
      minScore,
      maxScore,
      calculatedScores: {
        ...result.calculatedScores,
        allSubjectsRanked: mapBreakdownDetailsToRankedSubjects(currentBreakdown)
          ?? result.calculatedScores.allSubjectsRanked,
      },
      isEligible: (currentCalc?.ger?.eligible && currentCalc?.per?.eligible) ?? result.isEligible,
      recommendationRate: currentCalc?.recommendationRate ?? result.recommendationRate,
      ger: currentCalc?.ger ?? result.ger,
      per: currentCalc?.per ?? result.per,
    });
  }, [scores, selectedElectives]);

  const closeProgrammeDialog = () => {
    setDialogProgramme(null);
  };

  const toggleFavourite = useCallback(async (result: ProgrammeResult) => {
    if (!result.programmeEntry || !result.instKey) return;
    if (!(await requireSignIn("favourites"))) return;

    const progId = result.programmeEntry.programmeDetails.id;
    if (isFavourite(progId)) {
      removeFavourite(progId);
    } else {
      addFavourite(result.programmeEntry, result.instKey);
    }
  }, [isFavourite, addFavourite, removeFavourite, requireSignIn]);

  const filteredResults = useMemo(() => {
    const t0 = performance.now();
    const r = allResults.filter((result) => {
      // Institution buttons narrow every tab, including "all".
      if (universityFilter !== ALL_UNIVERSITIES &&
          result.university.toLowerCase() !== universityFilter) {
        return false;
      }
      for (const filter of ["LQ", "MED", "UQ"] as const) {
        if (!scoreQuartileFilters[filter]) continue;
        const quartile = result.admissionHistory
          .map((score) => score[filter])
          .map(Number)
          .find((value) => Number.isFinite(value) && value > 0);
        if (quartile !== undefined && result.currentScore < quartile) return false;
      }
      if (activeTab === "all") return true;
      if (activeTab === "recommended") {
        // Show AI-recommended programmes from the Recommended page
        if (preferences.recommendedRecommendations && preferences.recommendedRecommendations.length > 0) {
          const aiRecommendedIds = preferences.recommendedRecommendations.map((rec: any) =>
            rec.programme?.programmeDetails?.id || rec.jupasCode
          );
          return result.programmeEntry && aiRecommendedIds.includes(result.programmeEntry.programmeDetails.id);
        }
        return false;
      }
      if (activeTab === "favourites") {
        return result.programmeEntry && isFavourite(result.programmeEntry.programmeDetails.id);
      }
      if (activeTab === "eligible") return result.isEligible;
      if (activeTab === "new") {
        return result.new_programme === true;
      }
      return true;
    });
    console.log(`[perf] filteredResults: ${r.length}/${allResults.length} in ${(performance.now()-t0).toFixed(1)}ms`);
    return r;
  }, [allResults, activeTab, universityFilter, scoreQuartileFilters, preferences.recommendedRecommendations, isFavourite]);

  const availableQuartileFilters = useMemo(() => {
    const hasQuartile = (filter: ScoreQuartileFilter) => allResults.some((result) =>
      result.admissionHistory.some((score) => {
        const value = Number(score[filter]);
        return Number.isFinite(value) && value > 0;
      })
    );

    return {
      LQ: hasQuartile("LQ"),
      MED: hasQuartile("MED"),
      UQ: hasQuartile("UQ"),
    };
  }, [allResults]);

  // Card-view ordering follows the sort buttons / table headers (`sorting`),
  // with special handling for the AI-ranked recommended tab.
  const sortedResults = useMemo(() => {
    const t0 = performance.now();
    const activeSort = sorting[0];
    const sortId = activeSort?.id ?? 'university';
    const desc = activeSort?.desc ?? false;
    // Columns only sortable from the table headers fall back to likelihood order for cards.
    const dir = desc ? -1 : 1;
    let r: ProgrammeResult[];
    if (sortId === 'jupas_id') {
      r = [...filteredResults].sort((a, b) =>
        dir * String(a.jupas_id).localeCompare(String(b.jupas_id), undefined, { numeric: true }));
    } else if (sortId === 'programmeName') {
      r = [...filteredResults].sort((a, b) =>
        dir * a.programmeName.localeCompare(b.programmeName));
    } else if (sortId === 'currentScore' || sortId === 'score2025Lower' || sortId === 'score2025Median' || sortId === 'score2025Upper') {
      const scoreFor = (result: ProgrammeResult): number => {
        if (sortId === 'currentScore') return result.currentScore;
        const score = sortId === 'score2025Lower'
          ? result.score2025Lower
          : sortId === 'score2025Median'
            ? result.score2025Median
            : result.score2025Upper;
        const parsed = Number(score);
        return Number.isFinite(parsed) ? parsed : Number.NaN;
      };
      r = [...filteredResults].sort((a, b) => {
        const scoreA = scoreFor(a);
        const scoreB = scoreFor(b);
        if (Number.isNaN(scoreA)) return Number.isNaN(scoreB) ? 0 : 1;
        if (Number.isNaN(scoreB)) return -1;
        return dir * (scoreA - scoreB);
      });
    } else if (activeTab === "recommended" && preferences.recommendedRecommendations && preferences.recommendedRecommendations.length > 0) {
      // Create a map of programme ID to AI rank (lower number = better)
      const aiRankMap = new Map<string, number>();
      preferences.recommendedRecommendations.forEach((rec: any, index: number) => {
        const id = rec.programme?.programmeDetails?.id || rec.jupasCode;
        aiRankMap.set(id, index);
      });

      // Sort by AI rank (descending likelihood = best-ranked first)
      const rankDir = sortId === 'recommendationRate' && !desc ? -1 : 1;
      r = [...filteredResults].sort((a, b) => {
        const aId = a.programmeEntry?.programmeDetails.id || '';
        const bId = b.programmeEntry?.programmeDetails.id || '';
        const aRank = aiRankMap.get(aId) ?? 999999;
        const bRank = aiRankMap.get(bId) ?? 999999;
        return rankDir * (aRank - bRank);
      });
    } else if (sortId === 'university') {
      r = [...filteredResults].sort((a, b) => {
        const byUni = a.university.localeCompare(b.university);
        // Within one university, JUPAS code keeps the order stable and readable.
        return dir * (byUni !== 0
          ? byUni
          : String(a.jupas_id).localeCompare(String(b.jupas_id), undefined, { numeric: true }));
      });
    } else {
      const rateDir = sortId === 'recommendationRate' && !desc ? -1 : 1;
      r = [...filteredResults].sort((a, b) => rateDir * (b.recommendationRate - a.recommendationRate));
    }
    console.log(`[perf] sortedResults: ${r.length} in ${(performance.now()-t0).toFixed(1)}ms`);
    return r;
  }, [filteredResults, activeTab, preferences.recommendedRecommendations, sorting]);

  // Apply globalFilter for card view (table view uses TanStack's built-in getFilteredRowModel)
  const cardViewResults = useMemo(() => {
    if (!globalFilter) return sortedResults;
    const searchTerms = globalFilter.toLowerCase().split(/\s+/).filter(term => term.length > 0);
    if (searchTerms.length === 0) return sortedResults;

    const instituteNames = ['hku', 'cuhk', 'polyu', 'cityu', 'hkbu', 'lingnan', 'eduhk', 'hkust', 'hkmu', 'sssdp'];
    const hasInstituteFilter = searchTerms.some(term => instituteNames.includes(term));

    return sortedResults.filter(result => {
      if (hasInstituteFilter) {
        const matchedInstitute = searchTerms.find(term => instituteNames.includes(term));
        if (result.university.toLowerCase() !== matchedInstitute) return false;
        const otherTerms = searchTerms.filter(term => !instituteNames.includes(term));
        if (otherTerms.length === 0) return true;
        return otherTerms.every(term => {
          const termWithoutJS = term.startsWith('js') ? term.substring(2) : term;
          return result.programmeName.toLowerCase().includes(term) ||
            String(result.jupas_id).toLowerCase().includes(term) ||
            String(result.jupas_id).toLowerCase().includes(termWithoutJS) ||
            ('js' + String(result.jupas_id).toLowerCase()).includes(term) ||
            (result.programmeEntry?.programmeDetails.name_tch || '').toLowerCase().includes(term);
        });
      }
      return searchTerms.every(term => {
        const termWithoutJS = term.startsWith('js') ? term.substring(2) : term;
        return result.programmeName.toLowerCase().includes(term) ||
          result.university.toLowerCase().includes(term) ||
          result.jupas_id.toLowerCase().includes(term) ||
          result.jupas_id.toLowerCase().includes(termWithoutJS) ||
          ('js' + result.jupas_id.toLowerCase()).includes(term) ||
          (result.programmeEntry?.programmeDetails.name_tch || '').toLowerCase().includes(term);
      });
    });
  }, [sortedResults, globalFilter]);

  // Whether there are more results the user could potentially see by scrolling
  const hasMoreToShow = hasMoreToCompute || (isTableView
    ? displayCount < filteredResults.length
    : cardsToShow < cardViewResults.length);

  // Calculate counts for each tab
  const tabCounts = displayTabCounts;

  const tabs = [
    { key: "all" as const, label: translate('all'), count: tabCounts.all },
    { key: "recommended" as const, label: translate('recommended'), count: tabCounts.recommended, gated: "recommended" as const },
    { key: "favourites" as const, label: translate('favourites'), count: tabCounts.favourites, gated: "favourites" as const },
    { key: "eligible" as const, label: translate('eligible-tab'), count: tabCounts.eligible },
    { key: "new" as const, label: translate('newProgramme'), count: tabCounts.new },
  ];

  // Keep refs to each card for scroll behavior on expand/collapse
  const cardRefs = useRef<Record<string, HTMLDivElement | null>>({});

  // Define table columns
  const columns = useMemo<ColumnDef<ProgrammeResult>[]>(
    () => [
      {
        accessorKey: 'university',
        size: 100,
        minSize: 80,
        maxSize: 120,
        header: ({ column }) => (
          <div className="flex items-center cursor-pointer" onClick={(e) => { e.stopPropagation(); column.toggleSorting(); }}>
            {translate('university')}
            {column.getIsSorted() === 'asc' ? <ChevronUp className="ml-1 h-4 w-4" /> : column.getIsSorted() === 'desc' ? <ChevronDown className="ml-1 h-4 w-4" /> : <ChevronsUpDown className="ml-1 h-4 w-4" />}
          </div>
        ),
      },
      {
        accessorKey: 'jupas_id',
        size: 80,
        minSize: 70,
        maxSize: 100,
        header: ({ column }) => (
          <div className="flex items-center cursor-pointer" onClick={(e) => { e.stopPropagation(); column.toggleSorting(); }}>
            {translate('jupas_id')}
            {column.getIsSorted() === 'asc' ? <ChevronUp className="ml-1 h-4 w-4" /> : column.getIsSorted() === 'desc' ? <ChevronDown className="ml-1 h-4 w-4" /> : <ChevronsUpDown className="ml-1 h-4 w-4" />}
          </div>
        ),
        cell: ({ row }) => 'JS' + row.original.jupas_id,
      },

      {
        accessorKey: 'programmeName',
        size: 100, // Fixed width for ellipsis to work
        minSize: 150,
        maxSize: 200,
        header: ({ column }) => (
          <div className="flex items-center cursor-pointer" onClick={(e) => { e.stopPropagation(); column.toggleSorting(); }}>
            {translate('programmeName')}
            {column.getIsSorted() === 'asc' ? <ChevronUp className="ml-1 h-4 w-4" /> : column.getIsSorted() === 'desc' ? <ChevronDown className="ml-1 h-4 w-4" /> : <ChevronsUpDown className="ml-1 h-4 w-4" />}
          </div>
        ),
        cell: ({ row }) => (
          <div className="flex items-center justify-between gap-2">
            <Tooltip>
              <TooltipTrigger asChild>
                <span className="truncate cursor-help max-w-[100%] inline-block">{row.original.programmeName}</span>
              </TooltipTrigger>
              <TooltipContent className="max-w-md">
                <p className="whitespace-pre-wrap">{row.original.programmeName}</p>
              </TooltipContent>
            </Tooltip>
            <div className="flex items-center gap-1 flex-shrink-0">
              <NewBadge newProgramme={row.original.new_programme} jupas_id={row.original.jupas_id} />
            </div>
          </div>
        ),
      },

      {
        accessorKey: 'currentScore',
        sortingFn: 'basic',
        size: 80,
        minSize: 70,
        maxSize: 90,
        meta: { align: 'center' },
        header: ({ column }) => (
          <div className="flex items-center justify-center cursor-pointer" onClick={(e) => { e.stopPropagation(); column.toggleSorting(); }}>
            {translate('yourCurrentScore')}
            {column.getIsSorted() === 'asc' ? <ChevronUp className="ml-1 h-4 w-4" /> : column.getIsSorted() === 'desc' ? <ChevronDown className="ml-1 h-4 w-4" /> : <ChevronsUpDown className="ml-1 h-4 w-4" />}
          </div>
        ),
        cell: ({ row }) => <div className="text-center font-semibold">{formatScore(row.original.currentScore)}</div>,
      },
      {
        id: 'score2025Lower',
        accessorFn: (row) => {
          const score = Number(row.score2025Lower);
          return Number.isFinite(score) ? score : undefined;
        },
        sortUndefined: 'last',
        sortingFn: 'basic',
        size: 70,
        minSize: 60,
        maxSize: 80,
        meta: { align: 'center' },
        header: ({ column }) => (
          <div className="flex items-center justify-center cursor-pointer" onClick={(e) => { e.stopPropagation(); column.toggleSorting(); }}>
            {translate('lowerScore2025')}
            {column.getIsSorted() === 'asc' ? <ChevronUp className="ml-1 h-4 w-4" /> : column.getIsSorted() === 'desc' ? <ChevronDown className="ml-1 h-4 w-4" /> : <ChevronsUpDown className="ml-1 h-4 w-4" />}
          </div>
        ),
        cell: ({ row }) => <div className="text-center">{row.original.score2025Lower}</div>,
      },
      {
        id: 'score2025Median',
        accessorFn: (row) => {
          const score = Number(row.score2025Median);
          return Number.isFinite(score) ? score : undefined;
        },
        sortUndefined: 'last',
        sortingFn: 'basic',
        size: 70,
        minSize: 60,
        maxSize: 80,
        meta: { align: 'center' },
        header: ({ column }) => (
          <div className="flex items-center justify-center cursor-pointer" onClick={(e) => { e.stopPropagation(); column.toggleSorting(); }}>
            {translate('medianScore2025')}
            {column.getIsSorted() === 'asc' ? <ChevronUp className="ml-1 h-4 w-4" /> : column.getIsSorted() === 'desc' ? <ChevronDown className="ml-1 h-4 w-4" /> : <ChevronsUpDown className="ml-1 h-4 w-4" />}
          </div>
        ),
        cell: ({ row }) => <div className="text-center">{row.original.score2025Median}</div>,
      },
      {
        id: 'score2025Upper',
        accessorFn: (row) => {
          const score = Number(row.score2025Upper);
          return Number.isFinite(score) ? score : undefined;
        },
        sortUndefined: 'last',
        sortingFn: 'basic',
        size: 70,
        minSize: 60,
        maxSize: 80,
        meta: { align: 'center' },
        header: ({ column }) => (
          <div className="flex items-center justify-center cursor-pointer" onClick={(e) => { e.stopPropagation(); column.toggleSorting(); }}>
            {translate('upperScore2025')}
            {column.getIsSorted() === 'asc' ? <ChevronUp className="ml-1 h-4 w-4" /> : column.getIsSorted() === 'desc' ? <ChevronDown className="ml-1 h-4 w-4" /> : <ChevronsUpDown className="ml-1 h-4 w-4" />}
          </div>
        ),
        cell: ({ row }) => <div className="text-center">{row.original.score2025Upper}</div>,
      },

      {
        accessorKey: 'ger',
        size: 70,
        minSize: 60,
        maxSize: 80,
        meta: { align: 'center' },
        header: () => (
          <div className="flex items-center justify-center">
            {translate('ger')}
          </div>
        ),
        cell: ({ row }) => (
          <div className="text-center">
            <GerBadge ger={row.original.ger} translate={translate} />
          </div>
        ),
      },
      {
        accessorKey: 'per',
        size: 70,
        minSize: 60,
        maxSize: 80,
        meta: { align: 'center' },
        header: () => (
          <div className="flex items-center justify-center">
            {translate('per')}
          </div>
        ),
        cell: ({ row }) => (
          <div className="text-center">
            <PerBadge per={row.original.per} translate={translate} />
          </div>
        ),
      },
      {
        accessorKey: 'comments',
        size: 100,
        minSize: 100,
        maxSize: 100,
        header: ({ column }) => (
          <div className="flex items-center cursor-pointer" onClick={(e) => { e.stopPropagation(); column.toggleSorting(); }}>
            {translate('comments')}
            {column.getIsSorted() === 'asc' ? <ChevronUp className="ml-1 h-4 w-4" /> : column.getIsSorted() === 'desc' ? <ChevronDown className="ml-1 h-4 w-4" /> : <ChevronsUpDown className="ml-1 h-4 w-4" />}
          </div>
        ),
        cell: ({ row }) => {
          const comments = row.original.comments;
          if (!comments) return <div className="text-xs">-</div>;

          return (
            <Tooltip>
              <TooltipTrigger asChild>
                <div className="text-xs truncate cursor-help">
                  {comments}
                </div>
              </TooltipTrigger>
              <TooltipContent className="max-w-sm">
                <p className="whitespace-pre-wrap">{comments}</p>
              </TooltipContent>
            </Tooltip>
          );
        },
      },
      {
        id: 'favourite',
        accessorFn: (row) => row.programmeEntry && isFavourite(row.programmeEntry.programmeDetails.id) ? 1 : 0,
        sortingFn: 'basic',
        size: 60,
        minSize: 50,
        maxSize: 70,
        meta: { align: 'center' },
        header: ({ column }) => (
          <div className="flex items-center justify-center cursor-pointer" onClick={(e) => { e.stopPropagation(); column.toggleSorting(); }}>
            {translate('favourite')}
            {column.getIsSorted() === 'asc' ? <ChevronUp className="ml-1 h-4 w-4" /> : column.getIsSorted() === 'desc' ? <ChevronDown className="ml-1 h-4 w-4" /> : <ChevronsUpDown className="ml-1 h-4 w-4" />}
          </div>
        ),
        cell: ({ row }) => (
          <div
            className="text-center"
            onClick={(e) => e.stopPropagation()}
          >
            <Button
              size="sm"
              variant={row.original.programmeEntry && isFavourite(row.original.programmeEntry.programmeDetails.id) ? "default" : "outline"}
              onClick={(e) => {
                e.stopPropagation();
                toggleFavourite(row.original);
              }}
              title={isSignedIn ? undefined : translate('featureLoggedInOnly')}
              className={row.original.programmeEntry && isFavourite(row.original.programmeEntry.programmeDetails.id) ? "bg-red-500 hover:bg-red-600 text-white" : ""}
            >
              {isSignedIn ? (
                <Heart className={`h-4 w-4 ${row.original.programmeEntry && isFavourite(row.original.programmeEntry.programmeDetails.id) ? 'fill-current' : ''}`} />
              ) : (
                <Lock className="h-4 w-4 text-muted-foreground" />
              )}
            </Button>
          </div>
        ),
      },

    ],
    [language, isFavourite, translate, toggleFavourite, isSignedIn]
  );

  // Initialize table with sorted results — slicing handled by ResultsTableView
  const table = useReactTable({
    data: sortedResults,
    columns,
    state: {
      sorting,
      columnFilters,
      globalFilter,
    },
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    onGlobalFilterChange: setGlobalFilter,
    globalFilterFn: (row, columnId, filterValue) => {
      if (!filterValue) return true;
      // Split search terms by spaces and treat as AND logic
      const searchTerms = String(filterValue).toLowerCase().split(/\s+/).filter(term => term.length > 0);
      const result = row.original as ProgrammeResult;

      // Check if any search term is an exact match for a whole university name
      const instituteNames = ['hku', 'cuhk', 'polyu', 'cityu', 'hkbu', 'lingnan', 'eduhk', 'hkust', 'hkmu', 'sssdp'];
      const hasInstituteFilter = searchTerms.some(term => instituteNames.includes(term));

      // If a whole institute name is in the search, filter by that institute only
      if (hasInstituteFilter) {
        const matchedInstitute = searchTerms.find(term => instituteNames.includes(term));
        // University name must match exactly (case insensitive)
        if (result.university.toLowerCase() !== matchedInstitute) {
          return false;
        }
        // Check other search terms (excluding the institute name)
        const otherTerms = searchTerms.filter(term => !instituteNames.includes(term));
        if (otherTerms.length === 0) return true; // Only institute name searched
        return otherTerms.every(term =>
          result.programmeName.toLowerCase().includes(term) ||
          String(result.jupas_id).toLowerCase().includes(term) ||
          (result.programmeEntry?.programmeDetails.name_tch || '').toLowerCase().includes(term)
        );
      }

      // Normal search: all search terms must match at least one field
      return searchTerms.every(term => {
        const idStr = String(result.jupas_id).toLowerCase();
        const termWithoutJs = term.startsWith('js') ? term.slice(2) : term;
        return (
          result.programmeName.toLowerCase().includes(term) ||
          result.university.toLowerCase().includes(term) ||
          idStr.includes(term) ||
          idStr.includes(termWithoutJs) ||
          (result.programmeEntry?.programmeDetails.name_tch || '').includes(term)
        );
      });
    },
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
  });

  // console.log('filteredResults', filteredResults)
  // debugger;

  return (
    <Card className="p-2 sm:p-4 md:p-6 narrow-mt flex flex-col" style={{ maxHeight: 'calc(100vh - 2rem)' }}>

      {/* Header with Title and View Toggle */}
      <div className="flex items-center justify-between mb-4 flex-shrink-0">
        <h2 className="text-2xl font-bold">{translate('programmeResults')}</h2>

        {/* View Toggle Switch (Desktop only) */}
        <div className="flex flex-col items-end gap-1">
          <div
            className="flex items-center gap-3"
          >
            <LayoutGrid className={`h-5 w-5 ${!isTableView ? 'text-blue-600' : 'text-gray-400'}`} />

            <Switch.Root
              checked={isTableView}
              onCheckedChange={handleToggleView}
              className={`w-11 h-6 bg-gray-300 rounded-full relative data-[state=checked]:bg-blue-600 transition-colors outline-none cursor-pointer`}
            >
              <Switch.Thumb className="block w-5 h-5 bg-white rounded-full shadow-md transition-transform translate-x-0.5 data-[state=checked]:translate-x-[22px]" />
            </Switch.Root>

            <Table2 className={`h-5 w-5 ${isTableView ? 'text-blue-600' : 'text-gray-400'}`} />
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="text-xs md:text-sm lg:text-base flex gap-2 mb-0 border-b prog-res-tabs flex-shrink-0">
        {tabs.map((tab) => {
          // Recommended and Favourites are signed-in-only (requirement 3):
          // show a padlock instead of a count and ask for sign-in on click.
          const locked = Boolean(tab.gated) && !isSignedIn;

          return (
            <button
              key={tab.key}
              onClick={async () => {
                if (tab.gated && !(await requireSignIn(tab.gated))) return;
                onTabChange(tab.key);
              }}
              title={locked ? translate('featureLoggedInOnly') : undefined}
              className={`cursor-pointer w-[25%] px-2 py-2 md:px-4 font-medium transition-colors text-sm md:text-base ${activeTab === tab.key
                ? "border-b-2 border-blue-500 text-blue-600"
                : locked
                  ? "text-gray-400 hover:text-gray-600"
                  : "text-gray-600 hover:text-gray-900"
                }`}
            >
              {tab.label} <br />
              {locked ? (
                <Lock className="inline h-3.5 w-3.5" aria-label={translate('featureLoggedInOnly')} />
              ) : (
                <>({tab.count})</>
              )}
            </button>
          );
        })}
      </div>

      {/* Results Cards (shown when card view is selected) */}
      {!isTableView && <ResultsCardView
        isTableView={isTableView}
        globalFilter={globalFilter}
        setGlobalFilter={setGlobalFilter}
        universityFilter={universityFilter}
        setUniversityFilter={setUniversityFilter}
        scoreQuartileFilters={scoreQuartileFilters}
        availableQuartileFilters={availableQuartileFilters}
        setScoreQuartileFilters={setScoreQuartileFilters}
        isSignedIn={isSignedIn}
        requireSignIn={requireSignIn}
        sorting={sorting}
        setSorting={setSorting}
        scrollContainerRef={scrollContainerRef}
        sortedResults={cardViewResults}
        cardsToShow={cardsToShow}
        hasMoreResultsToLoad={hasMoreToShow}
        loadMoreResults={loadMoreResults}
        cardRefs={cardRefs}
        openProgrammeDialog={openProgrammeDialog}
        isFavourite={isFavourite}
        toggleFavourite={toggleFavourite}
        favouritesLocked={!isSignedIn}
        translate={translate}
        language={language}
        hasValidAdmissionScores={hasValidAdmissionScores}
        scores={scores}
        selectedElectives={selectedElectives}
      />}

      {/* Results Table (shown when table view is selected) */}
      {isTableView && <ResultsTableView
        isTableView={isTableView}
        globalFilter={globalFilter}
        setGlobalFilter={setGlobalFilter}
        universityFilter={universityFilter}
        setUniversityFilter={setUniversityFilter}
        scoreQuartileFilters={scoreQuartileFilters}
        availableQuartileFilters={availableQuartileFilters}
        setScoreQuartileFilters={setScoreQuartileFilters}
        isSignedIn={isSignedIn}
        requireSignIn={requireSignIn}
        sorting={sorting}
        setSorting={setSorting}
        scrollContainerRef={scrollContainerRef}
        translate={translate}
        table={table}
        displayCount={displayCount}
        hasMoreResultsToLoad={hasMoreToShow}
        loadMoreResults={loadMoreResults}
        openProgrammeDialog={openProgrammeDialog}
      />}

      {isCalculatingResults && (
        <div className="py-8 text-center text-gray-500">
          <div className="text-sm text-gray-400">
            {calculationProgress.processed}/{calculationProgress.total}
          </div>
        </div>
      )}

      {!isCalculatingResults && !hasMoreToCompute && sortedResults.length === 0 && (
          <div className="text-center py-8 text-gray-500">
            {translate('noProgrammesFoundFilter')}
          </div>
        )
      }

      {/* Loaded / Total counter */}
      {sortedResults.length > 0 && (
        <div className="py-2 text-center text-sm text-gray-400">
          {isTableView
            ? Math.min(displayCount, filteredResults.length)
            : Math.min(cardsToShow, cardViewResults.length)
          } / {filteredResults.length}
        </div>
      )}

      {/* Programme Details Dialog */}
      <ProgrammeDetailsDialog
        result={dialogProgramme}
        isOpen={dialogProgramme !== null}
        onClose={closeProgrammeDialog}
        selectedScoreType={dialogProgramme ? (selectedScoreType[dialogProgramme.id] || 'current') : 'current'}
        onScoreTypeChange={(type) => {
          if (dialogProgramme) {
            setSelectedScoreType(prev => ({ ...prev, [dialogProgramme.id]: type }));
          }
        }}
        scores={scores}
        selectedElectives={selectedElectives}
      />

    </Card >
  )
});

export default ResultsTable;
